import { extendTheme } from "@mui/joy";
import { createThemeLayout } from "./layout";
import { xsButtonRoot, xsIconButtonRoot } from "./xsButton";

/** The tooltip is the one surface with its own foreground pair and ring: in dark it is
 *  level2 on the chrome behind a hairline; in light it is an inverted dark box. */
declare module "@mui/joy/styles" {
    /** The app bar and rail around the content panel: one step darker than the content. The
     *  glass is what sits on the project's photo (Home's sample questions). */
    interface PaletteBackgroundOverrides {
        chrome: true;
        glass: true;
        field: true;
    }

    interface PaletteTextOverrides {
        tooltip: true;
        tooltipSecondary: true;
    }

    interface Palette {
        tooltipBorder: string;
        /** The dark scheme's one hairline where the content sheet and the rail meet the chrome
         *  (hp, 2026-10-28: "add a hairline, dark mode only"); transparent in light. */
        chromeEdge: string;
        /** A starred sheet's star, wherever a star toggle is drawn (`starToggleSx`,
         *  `pageStarToggleSx`). */
        star: string;
        /** The starred star's outline: the gold's own darker tone, which holds 3:1 on white
         *  where the gold alone does not. */
        starEdge: string;
    }
}

export type ColorScheme = "light" | "dark";
type RampName = "primary" | "neutral" | "danger" | "success" | "warning";
type RampStep = "50" | "100" | "200" | "300" | "400" | "500" | "600" | "700" | "800" | "900";
type ToneName = "danger" | "success" | "warning";

const RAMP_STEPS: readonly RampStep[] = [
    "50",
    "100",
    "200",
    "300",
    "400",
    "500",
    "600",
    "700",
    "800",
    "900",
];

/** One color ramp's ten steps plus the `rgb()` channel triplets Joy derives its alpha
 *  variants from (e.g. `rgba(var(--joy-palette-primary-mainChannel) / 0.22)`). */
type RampColors = Record<RampStep, string> & {
    mainChannel: string;
    lightChannel: string;
    darkChannel: string;
};

/** Every literal color one scheme needs. This is the one-system color contract
 *  (theme-proposal-2026-09-03); there is no `var(--joy-<scheme>-*)` indirection left
 *  to cross-reference against `tokens.css` — these values ARE the source of truth. */
interface SchemeColors {
    ramps: Record<RampName, RampColors>;
    primaryPlainColor: string;
    primarySoftBg: string;
    primarySolidColor: string;
    neutralOutlinedBorder: string;
    neutralOutlinedHoverBorder: string;
    /** A plain control's or row's hover and press: a wash of the ink, so it reads the same on
     *  the chrome, the content sheet and a popover. */
    neutralPlainHoverBg: string;
    neutralPlainActiveBg: string;
    /** success/warning/danger: the 12% wash of that tone's step-500 color. */
    toneSoftBg: Record<ToneName, string>;
    star: string;
    starEdge: string;
    background: {
        body: string;
        surface: string;
        /** The app bar and the rail (and the desktop tab bar): a step darker than the body. */
        chrome: string;
        /** The surface at 64% over the project's photo: primary text on it holds 4.5:1 over a
         *  white or a black photo alike (`tokens.contrast.test.ts`). */
        glass: string;
        /** A text field, select or search box: white in light, a step under the sheet in dark. */
        field: string;
        popup: string;
        level1: string;
        level2: string;
        level3: string;
        tooltip: string;
        backdrop: string;
    };
    text: {
        primary: string;
        secondary: string;
        tertiary: string;
        icon: string;
        tooltip: string;
        tooltipSecondary: string;
    };
    tooltipBorder: string;
    chromeEdge: string;
    divider: string;
    focusVisible: string;
}

// Primary: one accent for selection and primary actions (D3), white on it. Steps
// 500/600 are the solid and hover backgrounds; plainColor/softBg (set per-scheme below)
// are the link ink and the wash. theme-proposal-2026-09-03 §3 lightened the dark ink so
// every text pair on the app, the chrome and the wash measures 4.5:1 or more. Identical
// in both schemes — only the tone ramps below vary with light/dark.
const SHARED_PRIMARY_RAMP: RampColors = {
    "50": "#EDF5FD",
    "100": "#E3EFFB",
    "200": "#C7DFF7",
    "300": "#97C3F0",
    "400": "#4393E4",
    "500": "#2d6bff",
    "600": "#1f55d9",
    "700": "#12467B",
    "800": "#0A2744",
    "900": "#051423",
    mainChannel: "45 107 255",
    lightChannel: "199 223 247",
    darkChannel: "18 70 123",
};

// Also identical in both schemes.
const SHARED_NEUTRAL_RAMP: RampColors = {
    "50": "#FBFCFE",
    "100": "#F0F4F8",
    "200": "#DDE7EE",
    "300": "#CDD7E1",
    "400": "#9FA6AD",
    "500": "#636B74",
    "600": "#555E68",
    "700": "#32383E",
    "800": "#171A1C",
    "900": "#0B0D0E",
    mainChannel: "99 107 116",
    lightChannel: "221 231 238",
    darkChannel: "50 56 62",
};

// A starred star: gold, the same in both schemes, since a star sits on a sheet's page (paper in
// either theme) as often as on the theme's own ground. Not the warning tone, which reads as a
// late item (and is a muddy brown in light).
const STAR_GOLD = "#EAB308";
// Its outline: the gold is 1.9:1 on white (a sheet's page, the light ground), so the star is
// drawn with a darker gold edge that holds 3:1 there; on the dark ground the gold itself does.
const STAR_EDGE = "#A16207";

const DARK_COLORS: SchemeColors = {
    ramps: {
        primary: SHARED_PRIMARY_RAMP,
        neutral: SHARED_NEUTRAL_RAMP,
        // Tones: step 500 is the flat tone (text, border, plain ink); soft-bg is its 12% wash.
        danger: {
            "50": "#FEF6F6",
            "100": "#FCE4E4",
            "200": "#F7C5C5",
            "300": "#F09898",
            "400": "#E47474",
            "500": "#ef6b67",
            "600": "#A51818",
            "700": "#7D1212",
            "800": "#430A0A",
            "900": "#240505",
            mainChannel: "239 107 103",
            lightChannel: "247 197 197",
            darkChannel: "125 18 18",
        },
        success: {
            "50": "#F6FEF6",
            "100": "#E3FBE3",
            "200": "#C7F7C7",
            "300": "#A1E8A1",
            "400": "#51BC51",
            "500": "#5ec997",
            "600": "#136C13",
            "700": "#0A470A",
            "800": "#042F04",
            "900": "#021D02",
            mainChannel: "94 201 151",
            lightChannel: "199 247 199",
            darkChannel: "10 71 10",
        },
        warning: {
            "50": "#FEFAF6",
            "100": "#FDF0E1",
            "200": "#FCE1C2",
            "300": "#F3C896",
            "400": "#EA9A3E",
            "500": "#e7a83e",
            "600": "#72430D",
            "700": "#492B08",
            "800": "#2E1B05",
            "900": "#1D1002",
            mainChannel: "231 168 62",
            lightChannel: "252 225 194",
            darkChannel: "73 43 8",
        },
    },
    primaryPlainColor: "#6d9cff",
    primarySoftBg: "#1b2a4a",
    primarySolidColor: "#fff",
    neutralOutlinedBorder: "#2f374a",
    neutralOutlinedHoverBorder: "#3c4558",
    neutralPlainHoverBg: "rgba(200 215 255 / 0.07)",
    neutralPlainActiveBg: "rgba(200 215 255 / 0.11)",
    toneSoftBg: {
        danger: "color-mix(in srgb, #ef6b67 12%, transparent)",
        success: "color-mix(in srgb, #5ec997 12%, transparent)",
        warning: "color-mix(in srgb, #e7a83e 12%, transparent)",
    },
    star: STAR_GOLD,
    starEdge: STAR_EDGE,
    // The content panel is the body; the app bar and rail around it are the chrome, near black,
    // and a hairline (`chromeEdge`) parts them. Every surface leans a tinge toward the accent's
    // blue; the text stays neutral (Design v2 depth proposal, hp, 2026-10-28). Menus, popovers
    // and tooltips float on the popup tone.
    background: {
        body: "#131824",
        surface: "#131824",
        chrome: "#06090f",
        glass: "color-mix(in srgb, #131824 64%, transparent)",
        field: "#0e121c",
        popup: "#1c2130",
        level1: "#1e2331",
        level2: "#252c3b",
        level3: "#555E68",
        tooltip: "#282f3f",
        backdrop: "rgba(251 252 254 / 0.25)",
    },
    text: {
        primary: "#f5f7fa",
        secondary: "#c4cad4",
        tertiary: "#8d95a3",
        icon: "#8d95a3",
        // The tooltip box sits on the chrome; a ring separates it in dark.
        tooltip: "#f5f7fa",
        tooltipSecondary: "#c4cad4",
    },
    tooltipBorder: "#2b3142",
    chromeEdge: "rgba(255 255 255 / 0.1)",
    divider: "#222838",
    focusVisible: "#2d6bff",
};

const LIGHT_COLORS: SchemeColors = {
    ramps: {
        primary: SHARED_PRIMARY_RAMP,
        neutral: SHARED_NEUTRAL_RAMP,
        // Light tones (design-language-v2 §3.3, owner decision 1): the dark flat tones
        // fail WCAG AA on the light body, so step 500 takes pp-next's own light tones.
        danger: {
            "50": "#FEF6F6",
            "100": "#FCE4E4",
            "200": "#F7C5C5",
            "300": "#F09898",
            "400": "#E47474",
            "500": "#dc2626",
            "600": "#A51818",
            "700": "#7D1212",
            "800": "#430A0A",
            "900": "#240505",
            mainChannel: "239 107 103",
            lightChannel: "247 197 197",
            darkChannel: "125 18 18",
        },
        success: {
            "50": "#F6FEF6",
            "100": "#E3FBE3",
            "200": "#C7F7C7",
            "300": "#A1E8A1",
            "400": "#51BC51",
            "500": "#15803d",
            "600": "#136C13",
            "700": "#0A470A",
            "800": "#042F04",
            "900": "#021D02",
            mainChannel: "94 201 151",
            lightChannel: "199 247 199",
            darkChannel: "10 71 10",
        },
        warning: {
            "50": "#FEFAF6",
            "100": "#FDF0E1",
            "200": "#FCE1C2",
            "300": "#F3C896",
            "400": "#EA9A3E",
            "500": "#a16207",
            "600": "#72430D",
            "700": "#492B08",
            "800": "#2E1B05",
            "900": "#1D1002",
            mainChannel: "231 168 62",
            lightChannel: "252 225 194",
            darkChannel: "73 43 8",
        },
    },
    primaryPlainColor: "#1d4ed8",
    primarySoftBg: "rgba(37, 99, 235, 0.14)",
    primarySolidColor: "#fff",
    neutralOutlinedBorder: "#d4dae3",
    neutralOutlinedHoverBorder: "#a8b1c0",
    neutralPlainHoverBg: "rgba(15 23 42 / 0.06)",
    neutralPlainActiveBg: "rgba(15 23 42 / 0.1)",
    toneSoftBg: {
        danger: "color-mix(in srgb, #dc2626 12%, transparent)",
        success: "color-mix(in srgb, #15803d 12%, transparent)",
        warning: "color-mix(in srgb, #a16207 12%, transparent)",
    },
    star: STAR_GOLD,
    starEdge: STAR_EDGE,
    // Crisp light (Design v2 depth proposal, hp, 2026-10-28: "washed out, no dimensionality"):
    // the blue lives in the chrome and the accent; the content sheet is white, with one neutral
    // grey step inside it (level1) and near-black text. The contrast test holds the ratios.
    background: {
        body: "#ffffff",
        surface: "#ffffff",
        chrome: "#d9e2f0",
        glass: "color-mix(in srgb, #ffffff 64%, transparent)",
        field: "#ffffff",
        popup: "#fff",
        level1: "#ecf0f5",
        level2: "#e2e7ee",
        level3: "#CDD7E1",
        // Inverted tooltip surface (light only): the box separates from a white window
        // by its own dark surface, so it needs no ring.
        tooltip: "#151d2c",
        backdrop: "rgba(11 13 14 / 0.25)",
    },
    text: {
        primary: "#0b1220",
        secondary: "#3b4556",
        tertiary: "#667085",
        icon: "#667085",
        tooltip: "#f8fafc",
        tooltipSecondary: "#cbd5e1",
    },
    tooltipBorder: "transparent",
    chromeEdge: "transparent",
    divider: "#e4e8ee",
    focusVisible: "#2d6bff",
};

/** The one-system color contract for both schemes. Exported for tests only — production
 *  code reaches these values through `palette()` below. */
/** A popover's edge and lift (menus, chart hover cards): a ring in the outlined-border token
 *  and a soft drop toward the body tone. */
export const POPOVER_SHADOW =
    "0 0 0 1px var(--joy-palette-neutral-outlinedBorder), 0 18px 44px color-mix(in srgb, var(--joy-palette-background-body) 55%, transparent)";

export const THEME_COLORS: Record<ColorScheme, SchemeColors> = {
    dark: DARK_COLORS,
    light: LIGHT_COLORS,
};

function ramp(scheme: ColorScheme, name: RampName): Record<RampStep, string> {
    const colors = THEME_COLORS[scheme].ramps[name];
    return Object.fromEntries(RAMP_STEPS.map((step) => [step, colors[step]])) as Record<
        RampStep,
        string
    >;
}

function colorChannels(scheme: ColorScheme, name: RampName) {
    const colors = THEME_COLORS[scheme].ramps[name];
    return {
        mainChannel: colors.mainChannel,
        lightChannel: colors.lightChannel,
        darkChannel: colors.darkChannel,
    };
}

/** success/warning/danger: soft = the 12% wash of the tone; plain/outlined = the flat
 *  tone (step 500) itself, matching the ramp/background roles above — components read
 *  these instead of the static Joy `.100`/`.600` ramp steps, which do not change
 *  between color schemes. */
function toneRoles(scheme: ColorScheme, name: ToneName) {
    const colors = THEME_COLORS[scheme];
    const tone = colors.ramps[name]["500"];
    return {
        softBg: colors.toneSoftBg[name],
        softColor: tone,
        outlinedBorder: tone,
        plainColor: tone,
    };
}

function palette(scheme: ColorScheme) {
    const colors = THEME_COLORS[scheme];
    const accent = colors.ramps.primary["500"];
    const accentInk = colors.primaryPlainColor;
    const accentWash = colors.primarySoftBg;
    const text = colors.text.primary;
    const level1 = colors.background.level1;
    const level2 = colors.background.level2;
    return {
        primary: {
            ...ramp(scheme, "primary"),
            ...colorChannels(scheme, "primary"),
            plainColor: accentInk,
            plainHoverBg: accentWash,
            plainActiveBg: accentWash,
            softBg: accentWash,
            softColor: accentInk,
            softHoverBg: accentWash,
            softActiveBg: accentWash,
            solidBg: accent,
            solidColor: colors.primarySolidColor,
            // Darker under the pointer, so white on it keeps AA (a selected band, the primary button).
            solidHoverBg: colors.ramps.primary["600"],
        },
        neutral: {
            ...ramp(scheme, "neutral"),
            ...colorChannels(scheme, "neutral"),
            outlinedBorder: colors.neutralOutlinedBorder,
            outlinedHoverBorder: colors.neutralOutlinedHoverBorder,
            outlinedHoverBg: "transparent",
            outlinedActiveBg: "transparent",
            plainColor: text,
            plainHoverBg: colors.neutralPlainHoverBg,
            plainActiveBg: colors.neutralPlainActiveBg,
            softBg: level1,
            softColor: text,
            softHoverBg: level2,
            softActiveBg: level2,
        },
        danger: {
            ...ramp(scheme, "danger"),
            ...colorChannels(scheme, "danger"),
            ...toneRoles(scheme, "danger"),
        },
        success: {
            ...ramp(scheme, "success"),
            ...colorChannels(scheme, "success"),
            ...toneRoles(scheme, "success"),
        },
        warning: {
            ...ramp(scheme, "warning"),
            ...colorChannels(scheme, "warning"),
            ...toneRoles(scheme, "warning"),
        },
        background: {
            body: colors.background.body,
            surface: colors.background.surface,
            chrome: colors.background.chrome,
            glass: colors.background.glass,
            field: colors.background.field,
            popup: colors.background.popup,
            level1,
            level2,
            level3: colors.background.level3,
            tooltip: colors.background.tooltip,
            backdrop: colors.background.backdrop,
        },
        text: {
            primary: text,
            secondary: colors.text.secondary,
            tertiary: colors.text.tertiary,
            icon: colors.text.icon,
            tooltip: colors.text.tooltip,
            tooltipSecondary: colors.text.tooltipSecondary,
        },
        divider: colors.divider,
        focusVisible: colors.focusVisible,
        tooltipBorder: colors.tooltipBorder,
        chromeEdge: colors.chromeEdge,
        star: colors.star,
        starEdge: colors.starEdge,
    };
}

/** System body font. The token resolves in both hosts. */
const BODY_FONT_FAMILY = "var(--font), var(--joy-fontFamily-fallback)";

/** System display font. The display weight comes from `--display-weight`. */
const HEADING_FONT_FAMILY = "var(--display), var(--joy-fontFamily-fallback)";

/** Joy's default fallback. Sandboxed artifacts need this resolved value. */
const BODY_FONT_FALLBACK =
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/** Joy's `xl3`/`xl4` font-size steps have no v2 type role and no current consumer;
 *  they stay fixed rem sizes. Every other step maps to a `--type-*` role so Joy
 *  components respond live to `[data-text-size]`. */
const FONT_SIZE: Record<"xs" | "sm" | "md" | "lg" | "xl" | "xl2" | "xl3" | "xl4", string> = {
    xs: "var(--type-caption)",
    sm: "var(--type-secondary)",
    md: "var(--type-body)",
    lg: "var(--type-heading)",
    xl: "var(--type-title)",
    xl2: "var(--type-display)",
    xl3: "1.875rem",
    xl4: "2.25rem",
};

const DISPLAY_TYPOGRAPHY = {
    fontFamily: HEADING_FONT_FAMILY,
    fontSize: FONT_SIZE.xl2,
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: 0,
    color: "var(--joy-palette-text-primary)",
} as const;

const TITLE_TYPOGRAPHY = {
    fontFamily: HEADING_FONT_FAMILY,
    fontSize: FONT_SIZE.xl,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: 0,
    color: "var(--joy-palette-text-primary)",
} as const;

const HEADING_TYPOGRAPHY = {
    fontFamily: BODY_FONT_FAMILY,
    fontSize: FONT_SIZE.lg,
    fontWeight: 700,
    lineHeight: 1.33334,
    letterSpacing: 0,
    color: "var(--joy-palette-text-primary)",
} as const;

const BODY_TYPOGRAPHY = {
    fontFamily: BODY_FONT_FAMILY,
    fontSize: FONT_SIZE.md,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
    color: "var(--joy-palette-text-primary)",
} as const;

const SECONDARY_TYPOGRAPHY = {
    fontFamily: BODY_FONT_FAMILY,
    fontSize: FONT_SIZE.sm,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
    color: "var(--joy-palette-text-secondary)",
} as const;

const CAPTION_TYPOGRAPHY = {
    fontFamily: BODY_FONT_FAMILY,
    fontSize: FONT_SIZE.xs,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
    color: "var(--joy-palette-text-tertiary)",
} as const;

const TYPOGRAPHY = {
    display: DISPLAY_TYPOGRAPHY,
    title: TITLE_TYPOGRAPHY,
    heading: HEADING_TYPOGRAPHY,
    body: BODY_TYPOGRAPHY,
    secondary: SECONDARY_TYPOGRAPHY,
    caption: CAPTION_TYPOGRAPHY,
    overline: {
        ...CAPTION_TYPOGRAPHY,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
    },
    h1: DISPLAY_TYPOGRAPHY,
    h2: DISPLAY_TYPOGRAPHY,
    h3: { ...DISPLAY_TYPOGRAPHY, fontWeight: 600 },
    h4: { ...TITLE_TYPOGRAPHY, fontWeight: 600 },
    "title-lg": { ...HEADING_TYPOGRAPHY, fontWeight: 600 },
    "title-md": { ...BODY_TYPOGRAPHY, fontWeight: 500 },
    "title-sm": {
        ...SECONDARY_TYPOGRAPHY,
        fontWeight: 500,
        color: "var(--joy-palette-text-primary)",
    },
    "body-lg": { ...HEADING_TYPOGRAPHY, fontWeight: 400 },
    "body-md": BODY_TYPOGRAPHY,
    "body-sm": SECONDARY_TYPOGRAPHY,
    "body-xs": CAPTION_TYPOGRAPHY,
} as const;

/** Selected surfaces share the solid accent foreground, including secondary text
 * and icons that otherwise retain their unselected semantic color. */
const ON_BAND_TEXT = {
    "--joy-palette-text-primary": "var(--joy-palette-primary-solidColor)",
    "--joy-palette-text-secondary": "var(--joy-palette-primary-solidColor)",
    "--joy-palette-text-tertiary": "var(--joy-palette-primary-solidColor)",
    "--joy-palette-text-icon": "var(--joy-palette-primary-solidColor)",
} as const;

function selectedVariant<T extends object>(selected: boolean | undefined, variant: T) {
    return selected ? { ...variant, "&&": variant } : {};
}

/** Where you are: the solid accent, darker under the pointer and when pressed (hp,
 *  2026-10-28: "i like having blue as the selected - easy to follow .. we need a hover state"). */
function selectedBand(
    selected: boolean | undefined,
    theme: {
        variants: {
            solid: { primary: object };
            solidHover: { primary: object };
            solidActive: { primary: object };
        };
    },
) {
    return selectedVariant(selected, {
        ...theme.variants.solid.primary,
        ...ON_BAND_TEXT,
        "&:hover": theme.variants.solidHover.primary,
        "&:active": theme.variants.solidActive.primary,
    });
}

const CHART_PALETTE: ReadonlyArray<{ base: string; hover: string }> = [
    { base: "#60a5fa", hover: "#2563eb" },
    { base: "#34d399", hover: "#059669" },
    { base: "#fb923c", hover: "#ea580c" },
    { base: "#f87171", hover: "#dc2626" },
    { base: "#c084fc", hover: "#9333ea" },
    { base: "#94a3b8", hover: "#475569" },
];

/** Creates the one Joy theme for a host window. Font sizes and table row height
 * read `--type-*`/`--row-table` live, so `[data-text-size]`/`[data-density]` on
 * `<html>` restyle every Joy component without a new theme instance. */
export function createPrimeTheme() {
    const theme = extendTheme({
        spacing: 8,
        colorSchemes: {
            light: { palette: palette("light") },
            dark: { palette: palette("dark") },
        },
        typography: TYPOGRAPHY,
        components: {
            JoyAccordionGroup: { defaultProps: { size: "sm" } },
            JoyAlert: { defaultProps: { size: "sm" } },
            JoyAutocomplete: { defaultProps: { size: "sm" } },
            JoyAutocompleteListbox: { defaultProps: { size: "sm" } },
            JoyAvatar: { defaultProps: { size: "sm" } },
            JoyAvatarGroup: { defaultProps: { size: "sm" } },
            JoyBadge: { defaultProps: { size: "sm" } },
            JoyBreadcrumbs: { defaultProps: { size: "sm" } },
            JoyButtonGroup: { defaultProps: { size: "sm" } },
            JoyCheckbox: { defaultProps: { size: "sm" } },
            JoyCircularProgress: { defaultProps: { size: "sm" } },
            JoyDrawer: { defaultProps: { size: "sm" } },
            JoyFormControl: { defaultProps: { size: "sm" } },
            JoyLinearProgress: { defaultProps: { size: "sm" } },
            JoyList: { defaultProps: { size: "sm" } },
            JoyMenuButton: {
                defaultProps: { size: "sm" },
                styleOverrides: {
                    root: {
                        "--Button-minHeight": "var(--control)",
                        "--Icon-fontSize": "var(--glyph)",
                    },
                },
            },
            JoyMenuList: { defaultProps: { size: "sm" } },
            JoyModalClose: { defaultProps: { size: "sm" } },
            JoyModalDialog: { defaultProps: { size: "sm" } },
            JoyRadio: { defaultProps: { size: "sm" } },
            JoyRadioGroup: { defaultProps: { size: "sm" } },
            JoySlider: { defaultProps: { size: "sm" } },
            JoySnackbar: { defaultProps: { size: "sm" } },
            JoyStepper: { defaultProps: { size: "sm" } },
            JoySvgIcon: { defaultProps: { size: "sm" } },
            JoyTabList: { defaultProps: { size: "sm" } },
            JoyTabPanel: { defaultProps: { size: "sm" } },
            JoyTabs: { defaultProps: { size: "sm" } },
            JoyTextarea: { defaultProps: { size: "sm" } },
            JoyToggleButtonGroup: {
                defaultProps: { size: "sm", variant: "soft" },
                styleOverrides: {
                    root: ({ theme }) => ({
                        '& > button[aria-pressed="true"]': {
                            ...theme.variants.solid.primary,
                            "&:hover": theme.variants.solidHover.primary,
                        },
                    }),
                },
            },
            JoySwitch: {
                defaultProps: { size: "sm" },
                styleOverrides: {
                    input: {
                        top: 0,
                        left: 0,
                    },
                },
            },
            JoyTable: {
                defaultProps: {
                    size: "sm",
                    borderAxis: "none",
                    hoverRow: true,
                    stickyHeader: true,
                },
                styleOverrides: {
                    root: ({ theme }) => ({
                        "--TableRow-hoverBackground": theme.vars.palette.neutral.plainHoverBg,
                        "--TableCell-headBackground": theme.vars.palette.background.surface,
                        "--TableCell-paddingX": "4px",
                        "--TableCell-paddingY": "8px",
                        "& td:first-of-type, & th:first-of-type": {
                            paddingLeft: `var(--TableCell-gutterX, ${theme.layout.inset})`,
                        },
                        "& td:last-child, & th:last-child": {
                            paddingRight: `var(--TableCell-gutterX, ${theme.layout.inset})`,
                        },
                        // The eyebrow's voice (`tokens.css`: sentence case at 500), so the
                        // drawings' own titles are the one uppercase on the page (hp, 2026-10-28:
                        // "washed out and busy").
                        "& thead th": {
                            color: theme.vars.palette.text.tertiary,
                            fontSize: "var(--type-caption)",
                            fontWeight: "var(--eyebrow-weight)",
                            textTransform: "var(--eyebrow-case)",
                            letterSpacing: "var(--eyebrow-tracking)",
                            borderBottom: "1px solid",
                            borderColor: theme.vars.palette.divider,
                            height: "var(--row-table)",
                        },
                        "& tbody td": { height: "var(--row-table)" },
                        '& tbody tr[aria-selected="true"]': {
                            ...theme.variants.solid.primary,
                            ...ON_BAND_TEXT,
                        },
                        "& .rowActions": { opacity: 1 },
                        "@media (hover: hover) and (pointer: fine)": {
                            "& .rowActions": { opacity: 0 },
                        },
                        "& tr:hover .rowActions, & tr:focus-within .rowActions": {
                            opacity: 1,
                        },
                    }),
                },
            },
            JoyMenuItem: {
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        "--Icon-color": "var(--joy-palette-text-icon)",
                        ...selectedVariant(ownerState.selected, theme.variants.soft.neutral),
                    }),
                },
            },
            JoyOption: {
                styleOverrides: {
                    root: ({ ownerState, theme }) =>
                        selectedVariant(ownerState.selected, theme.variants.soft.neutral),
                },
            },
            JoyListSubheader: {
                styleOverrides: {
                    root: {
                        minBlockSize: 0,
                        paddingBlock: "6px 2px",
                        // The eyebrow's voice (`tokens.css`).
                        fontSize: "var(--type-caption)",
                        fontWeight: "var(--eyebrow-weight)",
                        textTransform: "var(--eyebrow-case)",
                        letterSpacing: "var(--eyebrow-tracking)",
                        color: "var(--joy-palette-text-tertiary)",
                    },
                },
            },
            JoyListDivider: {
                styleOverrides: {
                    root: { "--Divider-inset": "6px" },
                },
            },
            JoyListItemButton: {
                styleOverrides: {
                    root: ({ ownerState, theme }) => selectedBand(ownerState.selected, theme),
                },
            },
            JoyTab: {
                defaultProps: { disableIndicator: true },
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        borderRadius: "var(--radius-sm)",
                        ...selectedBand(ownerState.selected, theme),
                    }),
                },
            },
            JoyCard: {
                defaultProps: { size: "sm" },
                styleOverrides: {
                    root: {
                        boxShadow: "none",
                        border: "1px solid var(--joy-palette-divider)",
                        backgroundColor: "var(--joy-palette-background-surface)",
                    },
                },
            },
            JoyChip: {
                defaultProps: { size: "sm" },
            },
            JoySkeleton: {
                styleOverrides: {
                    root: { backgroundColor: "var(--joy-palette-background-level2)" },
                },
            },
            JoySelect: {
                defaultProps: { size: "sm" },
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.size === "sm"
                            ? { "--Select-minHeight": "var(--control)" }
                            : {}),
                        "--Icon-fontSize": "var(--glyph)",
                        backgroundColor: "var(--joy-palette-background-field)",
                    }),
                },
            },
            // Fields are flat: the field tone inside a border, no fill change on hover (only the
            // border darkens), so nothing but a button reads as pressable (hp, 2026-10-28).
            JoyInput: {
                defaultProps: { size: "sm", variant: "outlined" },
                styleOverrides: {
                    root: ({ ownerState }) => ({
                        ...(ownerState.size === "sm"
                            ? { "--Input-minHeight": "var(--control)" }
                            : {}),
                        "--Icon-fontSize": "var(--glyph)",
                        backgroundColor: "var(--joy-palette-background-field)",
                    }),
                },
            },
            // Flat buttons: the primary (solid) is the one filled at rest; every other button is its
            // icon and label, filled under the pointer and darker pressed (hp, 2026-10-28: "no bg
            // on secondary buttons"). `xs` is the light tool button (the viewer's footer): 24px,
            // a caption label in the secondary ink.
            JoyButton: {
                defaultProps: { size: "sm", variant: "plain" },
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        ...(ownerState.size === "sm"
                            ? { "--Button-minHeight": "var(--control)" }
                            : {}),
                        "--Icon-fontSize": "var(--glyph)",
                        ...xsButtonRoot(ownerState.size, theme),
                    }),
                },
            },
            JoyIconButton: {
                defaultProps: { size: "sm" },
                styleOverrides: {
                    root: ({ theme, ownerState }) => ({
                        // The house small default follows desktop density. Explicit
                        // touch sizes (for example TabletShell's `size="lg"`) retain
                        // Joy's geometry instead of being collapsed to `--control`.
                        ...(ownerState?.size === "sm"
                            ? { "--IconButton-size": "var(--control)" }
                            : {}),
                        "--Icon-fontSize": "var(--glyph)",
                        ...xsIconButtonRoot(ownerState?.size, theme),
                        '&[aria-pressed="true"]': {
                            ...theme.variants.soft.primary,
                            "&:hover": theme.variants.softHover.primary,
                        },
                    }),
                },
            },
            JoyMenu: {
                defaultProps: { size: "sm", variant: "plain" },
                styleOverrides: {
                    root: {
                        "--List-padding": "4px",
                        "--List-radius": "var(--radius-md)",
                        "--ListItem-radius": "var(--radius-sm)",
                        "--ListItem-minHeight": "var(--row-menu)",
                        "--ListItem-paddingY": "4px",
                        "--ListItem-paddingX": "9px",
                        "--ListItem-gap": "9px",
                        "--ListItemDecorator-size": "25px",
                        "--ListDivider-gap": "4px",
                        "--Icon-fontSize": "16px",
                        backgroundColor: "var(--joy-palette-background-popup)",
                        color: "var(--joy-palette-text-primary)",
                        boxShadow: POPOVER_SHADOW,
                        fontSize: "var(--type-secondary)",
                    },
                },
            },
            JoyTooltip: {
                defaultProps: { size: "sm" },
                styleOverrides: {
                    root: {
                        backgroundColor: "var(--joy-palette-background-tooltip)",
                        color: "var(--joy-palette-text-tooltip)",
                        border: "1px solid var(--joy-palette-tooltipBorder)",
                    },
                    arrow: {
                        "&::before": {
                            borderTopColor: "var(--joy-palette-background-tooltip)",
                            borderRightColor: "var(--joy-palette-background-tooltip)",
                        },
                    },
                },
            },
        },
        fontFamily: {
            body: BODY_FONT_FAMILY,
            display: HEADING_FONT_FAMILY,
            code: "var(--mono), var(--joy-fontFamily-fallback)",
        },
        fontWeight: {
            xl: "var(--display-weight)",
        },
        fontSize: FONT_SIZE,
        radius: {
            xs: "var(--radius-xs)",
            sm: "var(--radius-sm)",
            md: "var(--radius-md)",
            lg: "var(--radius-lg)",
            xl: "var(--radius-lg)",
        },
    });

    theme.layout = createThemeLayout(theme.spacing);
    return theme;
}

/** The one Joy theme instance. Every host shares it. */
export const primeTheme = createPrimeTheme();

/** The `--site-*` values a sandboxed Marvin artifact is drawn with, for one scheme. The frame
 *  cannot read the app's CSS variables, so they are resolved here; Marvin's interactive-artifact
 *  skill (ai_engine `agent/skills/interactive-artifact/SKILL.md`) draws with nothing else. */
export interface ArtifactTokens {
    /** The frame's own `color-scheme`, the app's resolved one. A frame whose scheme differs from
     *  the page's is painted on an opaque canvas of its own scheme: a white box on a dark page. */
    colorScheme: ColorScheme;
    /** A panel artifact's page (panels are light); an inline one is transparent over its message. */
    page: string;
    text: string;
    textMuted: string;
    primary: string;
    primaryHover: string;
    border: string;
    surface: string;
    radius: string;
    fontFamily: string;
    fontFallback: string;
    headingFont: string;
    danger: string;
    dangerHover: string;
    success: string;
    successHover: string;
    warning: string;
    warningHover: string;
    chartPalette: ReadonlyArray<{ base: string; hover: string }>;
}

/** A scheme's artifact tokens, from the same colors the app draws that scheme with. */
export function artifactTokens(scheme: ColorScheme): ArtifactTokens {
    const colors = palette(scheme);
    return {
        colorScheme: scheme,
        page: colors.background.surface,
        text: colors.text.primary,
        textMuted: colors.text.tertiary,
        primary: colors.primary.solidBg,
        primaryHover: colors.primary.solidHoverBg,
        border: colors.divider,
        surface: colors.background.level1,
        radius: "8px",
        fontFamily: BODY_FONT_FALLBACK,
        fontFallback: BODY_FONT_FALLBACK,
        headingFont: BODY_FONT_FALLBACK,
        danger: colors.danger["500"],
        dangerHover: colors.danger["600"],
        success: colors.success["500"],
        successHover: colors.success["600"],
        warning: colors.warning["500"],
        warningHover: colors.warning["600"],
        chartPalette: CHART_PALETTE,
    };
}
